import React, { useEffect } from "react";
import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Menu, Settings, LogOut, ChevronDown, Info, User } from "lucide-react";
import styles from "../../styles/__dashboardLayout.module.scss";
import { IoNotifications } from "react-icons/io5";
import { HiMiniLanguage } from "react-icons/hi2";
import Logo from "../../assets/images/logo.png";
import UserAvatar from "../../assets/images/user.png";
import { BsChatText } from "react-icons/bs";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../reducer/logout/logoutReducer";
import { useSocketNotifications } from "../../hooks/useSocketNotifications";
import PharmacyNotificationsPanel from "../pharmacy/notifications/PharmacyNotificationsPanel";
import PharmacyChatDock from "../pharmacy-chat/PharmacyChatDock";
import { getPharmacyUnreadNotificationCount } from "../../slice/pharmacy/notifications/pharmacyNotificationsSlice";
import { useTranslation } from "react-i18next";
import { toast } from "../app-toast";
import defaultAvatar from "../../assets/images/default_avatar.png";
import axios from "axios";
import {
  isChatOpen,
  OPEN_CHAT_MESSAGE_EVENT,
  sameId,
} from "../../utils/activeChatRegistry";
import useAdminChatUnread from "../../hooks/useAdminChatUnread";
import { isAdminSupportMessage } from "../../utils/chatEvents";
import { getTokenUserId } from "../../utils/socketAuth";

const PharmacyDashboardLayout = ({
  children,
  currentPage,
  onNavigate,
  onLogout,
}) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("English");
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] =
    useState(false);
  const [isPharmacyNotificationsOpen, setIsPharmacyNotificationsOpen] =
    useState(false);
  const [headerAvatar, setHeaderAvatar] = useState(
    () => sessionStorage.getItem("pharmacy_avatar_url") || defaultAvatar,
  );
  // Pharmacy center name + email shown next to the avatar / in the header dropdown.
  const [headerName, setHeaderName] = useState(
    () => sessionStorage.getItem("pharmacy_name") || "",
  );
  const [headerPhone, setHeaderPhone] = useState(
    () => sessionStorage.getItem("pharmacy_phone") || "",
  );

  const [openMenu, setOpenMenu] = useState(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Pharmacy notifications from Redux
  const { unreadCount: pharmacyUnreadCount } = useSelector(
    (state) =>
      state.pharmacyNotifications || {
        unreadCount: 0,
      },
  );

  // Debug log for unread count changes
  useEffect(() => {
    console.log(
      "[PHARMACY_LAYOUT] Pharmacy unread count changed:",
      pharmacyUnreadCount,
    );
  }, [pharmacyUnreadCount]);

  // Socket notifications hook
  const {
    // eslint-disable-next-line
    notifications,
    // eslint-disable-next-line
    isConnected,
    socket: notifSocket,
    // eslint-disable-next-line
    clearNotifications,
    // eslint-disable-next-line
    removeNotification,
    // eslint-disable-next-line
    markAsRead,
  } = useSocketNotifications();

  // Load pharmacy notifications and unread count on component mount
  useEffect(() => {
    dispatch(getPharmacyUnreadNotificationCount());

    // Refresh unread count every 30 seconds
    const interval = setInterval(() => {
      dispatch(getPharmacyUnreadNotificationCount());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Refresh unread count when notifications change (real-time updates)
  useEffect(() => {
    if (isConnected) {
      dispatch(getPharmacyUnreadNotificationCount());
    }
  }, [notifications, isConnected, dispatch]);

  // Sidebar "Chat" badge = unread messages from the admin team in the support chat only
  // (room_context "admin_provider"). Order chats and general patient chats have their own
  // badges (order list + chat dock) and never count here. Loaded from the server, so it
  // survives navigation. (The old "no appointment_id" test also counted general patient chats.)
  const { count: chatUnreadCount } = useAdminChatUnread({
    socket: notifSocket,
    onChatPage: location.pathname === "/pharmacy-chat",
    onAdminMessage: () => {
      try {
        const audio = new Audio(
          require("../../assets/notification/alert1.mp3"),
        );
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {}
      toast.info(t("pharmacy.sidebar.chatToast.newAdminChat"), {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
      });
    },
  });

  // Patient messages (order chats and general chats): toast + unread badges + chat dock.
  useEffect(() => {
    if (!notifSocket) return;

    const handleNewChatMessage = (data) => {
      // Ignore our own messages.
      if (sameId(data?.sender_id, getTokenUserId())) return;
      // Admin/support messages drive the sidebar Chat badge (useAdminChatUnread above).
      if (isAdminSupportMessage(data)) return;

      // The pharmacy already has this conversation open (order list / order details chat
      // or an expanded dock bubble): hand the message to that chat window and skip the
      // sound, the toast and the dock bubble. The open chat de-duplicates it against its
      // own socket and marks the room read (which re-syncs the badges).
      if (
        isChatOpen({
          roomId: data?.room_id,
          appointmentId: data?.appointment_id,
          senderId: data?.sender_id,
        })
      ) {
        window.dispatchEvent(
          new CustomEvent(OPEN_CHAT_MESSAGE_EVENT, { detail: data }),
        );
        return;
      }

      // Refresh the unread badges on the order list, and pop open a minimized chat dock so
      // the pharmacy can jump straight in. `appointment_id` is the integer pharmacy order id
      // (null for a general patient chat; the dock then resolves the patient by user id).
      window.dispatchEvent(
        new CustomEvent("chat-unread-refresh", {
          detail: {
            room_id: data?.room_id,
            appointment_id: data?.appointment_id,
          },
        }),
      );
      window.dispatchEvent(
        new CustomEvent("chat-incoming-message", {
          detail: {
            room_id: data?.room_id,
            appointment_id: data?.appointment_id,
            sender_id: data?.sender_id,
            sender_name: data?.sender_name,
          },
        }),
      );

      // Play notification sound
      try {
        const audio = new Audio(
          require("../../assets/notification/alert1.mp3"),
        );
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {}
      // Show notification that a new chat has arrived (bilingual)
      const senderName =
        data?.sender_name || t("pharmacy.sidebar.chatToast.patientFallback");
      toast.info(
        t("pharmacy.sidebar.chatToast.newMessageFrom", { senderName }),
        {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
        },
      );
    };

    notifSocket.on("new_message", handleNewChatMessage);
    return () => {
      notifSocket.off("new_message", handleNewChatMessage);
    };
  }, [notifSocket, i18n.language]);

  // Fetch pharmacy profile avatar for header
  const fetchPharmacyAvatar = React.useCallback(async () => {
    try {
      const token = sessionStorage.getItem("usr_1a2b3c");
      const axiosInstance = axios.create({
        baseURL: process.env.REACT_APP_API_URL,
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      const response = await axiosInstance.get("/api/v1/pharmacy/profile");
      if (response.data?.data) {
        const profile = response.data.data;
        // Cache + show the pharmacy center name in the header.
        const pharmacyName =
          profile.pharmacy_name_english ||
          profile.pharmacy_name ||
          profile.name ||
          "";
        if (pharmacyName) {
          setHeaderName(pharmacyName);
          sessionStorage.setItem("pharmacy_name", pharmacyName);
        }
        const pharmacyPhone =
          profile.phone ||
          profile.phone_number ||
          profile.mobile_number ||
          profile.contact_number ||
          profile.mobile ||
          profile.pharmacy_phone ||
          "";
        if (pharmacyPhone) {
          setHeaderPhone(pharmacyPhone);
          sessionStorage.setItem("pharmacy_phone", pharmacyPhone);
        }
        if (profile.avatar_presigned_url) {
          setHeaderAvatar(profile.avatar_presigned_url);
          sessionStorage.setItem(
            "pharmacy_avatar_url",
            profile.avatar_presigned_url,
          );
        } else if (profile.avatar_url) {
          setHeaderAvatar(profile.avatar_url);
          sessionStorage.setItem("pharmacy_avatar_url", profile.avatar_url);
        } else {
          setHeaderAvatar(defaultAvatar);
          sessionStorage.removeItem("pharmacy_avatar_url");
        }
      }
    } catch (error) {
      console.error("Error fetching pharmacy avatar:", error);
      setHeaderAvatar(defaultAvatar);
    }
  }, []);

  // Fetch avatar on mount and listen for updates. Only fetch when there's no
  // cached avatar to avoid re-fetching (and visibly reloading/flickering) the
  // header avatar on every page navigation, since the layout remounts per page.
  // The profile page fires 'pharmacyAvatarUpdated' to force a refresh on change.
  useEffect(() => {
    if (
      !sessionStorage.getItem("pharmacy_avatar_url") ||
      !sessionStorage.getItem("pharmacy_name") ||
      !sessionStorage.getItem("pharmacy_phone")
    ) {
      fetchPharmacyAvatar();
    }
    const handleAvatarUpdate = () => fetchPharmacyAvatar();
    window.addEventListener("pharmacyAvatarUpdated", handleAvatarUpdate);
    return () =>
      window.removeEventListener("pharmacyAvatarUpdated", handleAvatarUpdate);
  }, [fetchPharmacyAvatar]);

  // Toggle pharmacy notifications panel
  const togglePharmacyNotifications = () => {
    setIsPharmacyNotificationsOpen(!isPharmacyNotificationsOpen);
  };

  const logoutHandler = () => {
    const access_token = sessionStorage.getItem("usr_1a2b3c");
    const refresh_token = sessionStorage.getItem("usr_4d5e6f");
    dispatch(logout({ access_token, refresh_token }))
      .then((response) => {
        console.log("response:", response);
        const status =
          response?.payload?.data?.status_code ||
          response?.payload?.status_code;
        if (status === 200) {
          sessionStorage.removeItem("usr_1a2b3c");
          sessionStorage.removeItem("usr_4d5e6f");
          sessionStorage.removeItem("usr_role_id");
          sessionStorage.clear();
          navigate("/");
        }
      })
      .catch((error) => {
        console.log("error in logout: ", error);
      });
  };

  const languages = [
    { code: "en", name: "English", flag: "Flag_of_United_States-64x34.png" },
    { code: "ar", name: "العربية", flag: "Flag_of_Iraq-64x43.png" },
  ];

  // Notifications are now managed by the useSocketNotifications hook

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const handleLanguageSelect = (language) => {
    setSelectedLanguage(language.name);
    i18n.changeLanguage(language.code);
    changeLanguage(language.code);
    setIsLanguageDropdownOpen(false);
  };

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    document.dir = lang === "ar" ? "rtl" : "ltr";
  };

  // Set document direction on mount based on current language
  useEffect(() => {
    document.dir = i18n.language === "ar" ? "rtl" : "ltr";
  }, [i18n.language]);

  // Sync selectedLanguage with i18n.language on mount and when language changes
  useEffect(() => {
    const currentLang = languages.find((lang) => lang.code === i18n.language);
    if (currentLang) {
      setSelectedLanguage(currentLang.name);
    }
  }, [i18n.language]);

  // eslint-disable-next-line
  const toggleNotificationDropdown = () => {
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen);
  };

  // eslint-disable-next-line
  const getNotificationIcon = (iconType) => {
    switch (iconType) {
      case "calendar-blue":
        return (
          <span className="calendar-blue-icon notification-info-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2656_10702)">
                <mask
                  id="mask0_2656_10702"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <path d="M24 0H0V24H24V0Z" fill="white"></path>
                </mask>
                <g mask="url(#mask0_2656_10702)">
                  <path
                    d="M8 5.75C7.59 5.75 7.25 5.41 7.25 5V2C7.25 1.59 7.59 1.25 8 1.25C8.41 1.25 8.75 1.59 8.75 2V5C8.75 5.41 8.41 5.75 8 5.75Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M16 5.75C15.59 5.75 15.25 5.41 15.25 5V2C15.25 1.59 15.59 1.25 16 1.25C16.41 1.25 16.75 1.59 16.75 2V5C16.75 5.41 16.41 5.75 16 5.75Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M8.5 14.5009C8.37 14.5009 8.24 14.4709 8.12 14.4209C7.99 14.3709 7.89001 14.3009 7.79001 14.2109C7.61001 14.0209 7.5 13.7609 7.5 13.5009C7.5 13.2409 7.61001 12.9809 7.79001 12.7909C7.89001 12.7009 8 12.6309 8.12 12.5809C8.36 12.4809 8.64 12.4809 8.88 12.5809C9 12.6309 9.10999 12.7009 9.20999 12.7909C9.24999 12.8409 9.29999 12.8909 9.32999 12.9409C9.36999 13.0009 9.40001 13.0609 9.42001 13.1209C9.45001 13.1809 9.47001 13.2409 9.48001 13.3009C9.49001 13.3709 9.5 13.4409 9.5 13.5009C9.5 13.7609 9.38999 14.0209 9.20999 14.2109C9.10999 14.3009 9 14.3709 8.88 14.4209C8.76 14.4709 8.63 14.5009 8.5 14.5009Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M12 14.4992C11.87 14.4992 11.74 14.4692 11.62 14.4192C11.49 14.3692 11.39 14.2992 11.29 14.2092C11.11 14.0192 11 13.7592 11 13.4992C11 13.4392 11.01 13.3692 11.02 13.2992C11.03 13.2392 11.05 13.1792 11.08 13.1192C11.1 13.0592 11.13 12.9992 11.17 12.9392C11.21 12.8892 11.25 12.8392 11.29 12.7892C11.66 12.4192 12.33 12.4192 12.71 12.7892C12.75 12.8392 12.79 12.8892 12.83 12.9392C12.87 12.9992 12.9 13.0592 12.92 13.1192C12.95 13.1792 12.97 13.2392 12.98 13.2992C12.99 13.3692 13 13.4392 13 13.4992C13 13.7592 12.89 14.0192 12.71 14.2092C12.52 14.3892 12.27 14.4992 12 14.4992Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M8.5 18.0009C8.37 18.0009 8.24 17.9709 8.12 17.9209C8 17.8709 7.89001 17.8009 7.79001 17.7109C7.61001 17.5209 7.5 17.2609 7.5 17.0009C7.5 16.9409 7.50999 16.8709 7.51999 16.8109C7.52999 16.7409 7.54999 16.6809 7.57999 16.6209C7.59999 16.5609 7.63001 16.5009 7.67001 16.4409C7.70001 16.3909 7.75001 16.3409 7.79001 16.2909C7.89001 16.2009 8 16.1309 8.12 16.0809C8.36 15.9809 8.64 15.9809 8.88 16.0809C9 16.1309 9.10999 16.2009 9.20999 16.2909C9.24999 16.3409 9.29999 16.3909 9.32999 16.4409C9.36999 16.5009 9.40001 16.5609 9.42001 16.6209C9.45001 16.6809 9.47001 16.7409 9.48001 16.8109C9.49001 16.8709 9.5 16.9409 9.5 17.0009C9.5 17.2609 9.38999 17.5209 9.20999 17.7109C9.10999 17.8009 9 17.8709 8.88 17.9209C8.76 17.9709 8.63 18.0009 8.5 18.0009Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M20.5 9.83984H3.5C3.09 9.83984 2.75 9.49984 2.75 9.08984C2.75 8.67984 3.09 8.33984 3.5 8.33984H20.5C20.91 8.33984 21.25 8.67984 21.25 9.08984C21.25 9.49984 20.91 9.83984 20.5 9.83984Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M15.8205 22.7796C15.4405 22.7796 15.0805 22.6396 14.8205 22.3796C14.5105 22.0696 14.3705 21.6196 14.4405 21.1496L14.6305 19.7996C14.6805 19.4496 14.8905 19.0296 15.1405 18.7796L18.6804 15.2396C19.1604 14.7596 19.6305 14.5096 20.1405 14.4596C20.7705 14.3996 21.3804 14.6596 21.9604 15.2396C22.5704 15.8496 23.3904 17.0896 21.9604 18.5196L18.4205 22.0596C18.1705 22.3096 17.7504 22.5196 17.4004 22.5696L16.0504 22.7596C15.9704 22.7696 15.9005 22.7796 15.8205 22.7796ZM20.3104 15.9496C20.3004 15.9496 20.2904 15.9496 20.2804 15.9496C20.1404 15.9596 19.9504 16.0896 19.7404 16.2996L16.2005 19.8396C16.1705 19.8696 16.1204 19.9696 16.1204 20.0096L15.9405 21.2596L17.1905 21.0796C17.2305 21.0696 17.3304 21.0196 17.3604 20.9896L20.9004 17.4496C21.3404 17.0096 21.4004 16.7896 20.9004 16.2896C20.7404 16.1396 20.5104 15.9496 20.3104 15.9496Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M20.9206 19.2509C20.8506 19.2509 20.7806 19.2409 20.7206 19.2209C19.4006 18.8509 18.3506 17.8009 17.9806 16.4809C17.8706 16.0809 18.1006 15.6709 18.5006 15.5509C18.9006 15.4409 19.3106 15.6709 19.4306 16.0709C19.6606 16.8909 20.3106 17.5409 21.1306 17.7709C21.5306 17.8809 21.7606 18.3009 21.6506 18.7009C21.5506 19.0309 21.2506 19.2509 20.9206 19.2509Z"
                    fill="#2B7FFF"
                  ></path>
                  <path
                    d="M12 22.75H8C4.35 22.75 2.25 20.65 2.25 17V8.5C2.25 4.85 4.35 2.75 8 2.75H16C19.65 2.75 21.75 4.85 21.75 8.5V12C21.75 12.41 21.41 12.75 21 12.75C20.59 12.75 20.25 12.41 20.25 12V8.5C20.25 5.64 18.86 4.25 16 4.25H8C5.14 4.25 3.75 5.64 3.75 8.5V17C3.75 19.86 5.14 21.25 8 21.25H12C12.41 21.25 12.75 21.59 12.75 22C12.75 22.41 12.41 22.75 12 22.75Z"
                    fill="#2B7FFF"
                  ></path>
                </g>
              </g>
              <defs>
                <clipPath id="clip0_2656_10702">
                  <rect width="24" height="24" fill="white"></rect>
                </clipPath>
              </defs>
            </svg>
          </span>
        );
      case "calendar-red":
        return (
          <span className="calendar-red-icon notification-info-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_2656_10702)">
                <mask
                  id="mask0_2656_10702"
                  maskUnits="userSpaceOnUse"
                  x="0"
                  y="0"
                  width="24"
                  height="24"
                >
                  <path d="M24 0H0V24H24V0Z" fill="white"></path>
                </mask>
                <g mask="url(#mask0_2656_10702)">
                  <path
                    d="M8 5.75C7.59 5.75 7.25 5.41 7.25 5V2C7.25 1.59 7.59 1.25 8 1.25C8.41 1.25 8.75 1.59 8.75 2V5C8.75 5.41 8.41 5.75 8 5.75Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M16 5.75C15.59 5.75 15.25 5.41 15.25 5V2C15.25 1.59 15.59 1.25 16 1.25C16.41 1.25 16.75 1.59 16.75 2V5C16.75 5.41 16.41 5.75 16 5.75Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M8.5 14.5009C8.37 14.5009 8.24 14.4709 8.12 14.4209C7.99 14.3709 7.89001 14.3009 7.79001 14.2109C7.61001 14.0209 7.5 13.7609 7.5 13.5009C7.5 13.2409 7.61001 12.9809 7.79001 12.7909C7.89001 12.7009 8 12.6309 8.12 12.5809C8.36 12.4809 8.64 12.4809 8.88 12.5809C9 12.6309 9.10999 12.7009 9.20999 12.7909C9.24999 12.8409 9.29999 12.8909 9.32999 12.9409C9.36999 13.0009 9.40001 13.0609 9.42001 13.1209C9.45001 13.1809 9.47001 13.2409 9.48001 13.3009C9.49001 13.3709 9.5 13.4409 9.5 13.5009C9.5 13.7609 9.38999 14.0209 9.20999 14.2109C9.10999 14.3009 9 14.3709 8.88 14.4209C8.76 14.4709 8.63 14.5009 8.5 14.5009Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M12 14.4992C11.87 14.4992 11.74 14.4692 11.62 14.4192C11.49 14.3692 11.39 14.2992 11.29 14.2092C11.11 14.0192 11 13.7592 11 13.4992C11 13.4392 11.01 13.3692 11.02 13.2992C11.03 13.2392 11.05 13.1792 11.08 13.1192C11.1 13.0592 11.13 12.9992 11.17 12.9392C11.21 12.8892 11.25 12.8392 11.29 12.7892C11.66 12.4192 12.33 12.4192 12.71 12.7892C12.75 12.8392 12.79 12.8892 12.83 12.9392C12.87 12.9992 12.9 13.0592 12.92 13.1192C12.95 13.1792 12.97 13.2392 12.98 13.2992C12.99 13.3692 13 13.4392 13 13.4992C13 13.7592 12.89 14.0192 12.71 14.2092C12.52 14.3892 12.27 14.4992 12 14.4992Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M8.5 18.0009C8.37 18.0009 8.24 17.9709 8.12 17.9209C8 17.8709 7.89001 17.8009 7.79001 17.7109C7.61001 17.5209 7.5 17.2609 7.5 17.0009C7.5 16.9409 7.50999 16.8709 7.51999 16.8109C7.52999 16.7409 7.54999 16.6809 7.57999 16.6209C7.59999 16.5609 7.63001 16.5009 7.67001 16.4409C7.70001 16.3909 7.75001 16.3409 7.79001 16.2909C7.89001 16.2009 8 16.1309 8.12 16.0809C8.36 15.9809 8.64 15.9809 8.88 16.0809C9 16.1309 9.10999 16.2009 9.20999 16.2909C9.24999 16.3409 9.29999 16.3909 9.32999 16.4409C9.36999 16.5009 9.40001 16.5609 9.42001 16.6209C9.45001 16.6809 9.47001 16.7409 9.48001 16.8109C9.49001 16.8709 9.5 16.9409 9.5 17.0009C9.5 17.2609 9.38999 17.5209 9.20999 17.7109C9.10999 17.8009 9 17.8709 8.88 17.9209C8.76 17.9709 8.63 18.0009 8.5 18.0009Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M20.5 9.83984H3.5C3.09 9.83984 2.75 9.49984 2.75 9.08984C2.75 8.67984 3.09 8.33984 3.5 8.33984H20.5C20.91 8.33984 21.25 8.67984 21.25 9.08984C21.25 9.49984 20.91 9.83984 20.5 9.83984Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M15.8205 22.7796C15.4405 22.7796 15.0805 22.6396 14.8205 22.3796C14.5105 22.0696 14.3705 21.6196 14.4405 21.1496L14.6305 19.7996C14.6805 19.4496 14.8905 19.0296 15.1405 18.7796L18.6804 15.2396C19.1604 14.7596 19.6305 14.5096 20.1405 14.4596C20.7705 14.3996 21.3804 14.6596 21.9604 15.2396C22.5704 15.8496 23.3904 17.0896 21.9604 18.5196L18.4205 22.0596C18.1705 22.3096 17.7504 22.5196 17.4004 22.5696L16.0504 22.7596C15.9704 22.7696 15.9005 22.7796 15.8205 22.7796ZM20.3104 15.9496C20.3004 15.9496 20.2904 15.9496 20.2804 15.9496C20.1404 15.9596 19.9504 16.0896 19.7404 16.2996L16.2005 19.8396C16.1705 19.8696 16.1204 19.9696 16.1204 20.0096L15.9405 21.2596L17.1905 21.0796C17.2305 21.0696 17.3304 21.0196 17.3604 20.9896L20.9004 17.4496C21.3404 17.0096 21.4004 16.7896 20.9004 16.2896C20.7404 16.1396 20.5104 15.9496 20.3104 15.9496Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M20.9206 19.2509C20.8506 19.2509 20.7806 19.2409 20.7206 19.2209C19.4006 18.8509 18.3506 17.8009 17.9806 16.4809C17.8706 16.0809 18.1006 15.6709 18.5006 15.5509C18.9006 15.4409 19.3106 15.6709 19.4306 16.0709C19.6606 16.8909 20.3106 17.5409 21.1306 17.7709C21.5306 17.8809 21.7606 18.3009 21.6506 18.7009C21.5506 19.0309 21.2506 19.2509 20.9206 19.2509Z"
                    fill="#FF2056"
                  ></path>
                  <path
                    d="M12 22.75H8C4.35 22.75 2.25 20.65 2.25 17V8.5C2.25 4.85 4.35 2.75 8 2.75H16C19.65 2.75 21.75 4.85 21.75 8.5V12C21.75 12.41 21.41 12.75 21 12.75C20.59 12.75 20.25 12.41 20.25 12V8.5C20.25 5.64 18.86 4.25 16 4.25H8C5.14 4.25 3.75 5.64 3.75 8.5V17C3.75 19.86 5.14 21.25 8 21.25H12C12.41 21.25 12.75 21.59 12.75 22C12.75 22.41 12.41 22.75 12 22.75Z"
                    fill="#FF2056"
                  ></path>
                </g>
              </g>
              <defs>
                <clipPath id="clip0_2656_10702">
                  <rect width="24" height="24" fill="white"></rect>
                </clipPath>
              </defs>
            </svg>
          </span>
        );
      case "edit-yellow":
        return (
          <span className="edit-yellow-icon notification-info-icon">
            <svg
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.4528 1.0415H11.7503C11.9161 1.0415 12.0751 1.10735 12.1923 1.22456C12.3095 1.34177 12.3753 1.50074 12.3753 1.6665C12.3753 1.83226 12.3095 1.99124 12.1923 2.10845C12.0751 2.22566 11.9161 2.2915 11.7503 2.2915H10.5003C8.51866 2.2915 7.09533 2.29317 6.01199 2.43817C4.94616 2.5815 4.30366 2.85484 3.82866 3.329C3.35449 3.804 3.08199 4.44567 2.93866 5.5115C2.79366 6.59484 2.79199 8.01817 2.79199 9.99984C2.79199 11.9815 2.79366 13.4048 2.93866 14.4882C3.08199 15.554 3.35533 16.1965 3.82949 16.6715C4.30449 17.1457 4.94616 17.4182 6.01199 17.5615C7.09533 17.7065 8.51866 17.7082 10.5003 17.7082C12.482 17.7082 13.9053 17.7065 14.9887 17.5615C16.0545 17.4182 16.697 17.1448 17.172 16.6707C17.6462 16.1957 17.9187 15.554 18.062 14.4882C18.207 13.4048 18.2087 11.9815 18.2087 9.99984V8.74984C18.2087 8.58408 18.2745 8.42511 18.3917 8.3079C18.5089 8.19069 18.6679 8.12484 18.8337 8.12484C18.9994 8.12484 19.1584 8.19069 19.2756 8.3079C19.3928 8.42511 19.4587 8.58408 19.4587 8.74984V10.0473C19.4587 11.9715 19.4587 13.479 19.3003 14.6557C19.1387 15.859 18.8003 16.809 18.0553 17.5548C17.3095 18.3007 16.3595 18.6382 15.1553 18.7998C13.9795 18.9582 12.472 18.9582 10.5478 18.9582H10.4528C8.52866 18.9582 7.02116 18.9582 5.84449 18.7998C4.64116 18.6382 3.69116 18.2998 2.94533 17.5548C2.19949 16.809 1.86199 15.859 1.70033 14.6548C1.54199 13.479 1.54199 11.9715 1.54199 10.0473V9.95234C1.54199 8.02817 1.54199 6.52067 1.70033 5.344C1.86199 4.14067 2.20033 3.19067 2.94533 2.44484C3.69116 1.699 4.64116 1.3615 5.84533 1.19984C7.02116 1.0415 8.52866 1.0415 10.4528 1.0415ZM14.4753 1.8965C15.0253 1.36171 15.7636 1.06488 16.5307 1.07019C17.2978 1.07551 18.0319 1.38255 18.5744 1.92492C19.1169 2.46729 19.4241 3.20139 19.4296 3.96847C19.435 4.73555 19.1383 5.47395 18.6037 6.024L13.0637 11.5648C12.7545 11.8732 12.5603 12.0682 12.3445 12.2365C12.0898 12.4355 11.8139 12.606 11.522 12.7448C11.2745 12.8623 11.0137 12.949 10.5995 13.0873L8.17866 13.894C7.95964 13.9669 7.72464 13.9775 7.49997 13.9244C7.27531 13.8713 7.06985 13.7568 6.90662 13.5935C6.74339 13.4303 6.62882 13.2249 6.57576 13.0002C6.52269 12.7755 6.53322 12.5405 6.60616 12.3215L7.41283 9.90067C7.55116 9.48567 7.63783 9.22567 7.75533 8.97817C7.89533 8.68595 8.06477 8.41178 8.26366 8.15567C8.43199 7.939 8.62616 7.74567 8.93533 7.4365L14.4753 1.8965ZM17.7195 2.77984C17.4065 2.46698 16.982 2.29123 16.5395 2.29123C16.0969 2.29123 15.6725 2.46698 15.3595 2.77984L15.0462 3.094C15.0639 3.17456 15.0912 3.26845 15.1278 3.37567C15.247 3.71984 15.4737 4.17317 15.9003 4.59984C16.3158 5.01547 16.8362 5.31067 17.4062 5.454L17.7195 5.14067C18.0324 4.82766 18.2081 4.40322 18.2081 3.96067C18.2081 3.51812 18.0324 3.09284 17.7195 2.77984ZM16.4212 6.43984C15.8964 6.2136 15.4196 5.88949 15.0162 5.48484C14.6115 5.08138 14.2874 4.60456 14.0612 4.07984L9.84866 8.2915C9.50116 8.639 9.36533 8.7765 9.24866 8.92484C9.10699 9.10884 8.98494 9.30714 8.88449 9.5165C8.80283 9.68567 8.74116 9.869 8.58533 10.3348L8.22616 11.414L9.08616 12.2748L10.1653 11.9148C10.632 11.759 10.8145 11.6973 10.9845 11.6165C11.1945 11.5165 11.3917 11.3946 11.5762 11.2507C11.7237 11.1348 11.8612 10.999 12.2087 10.6523L16.4212 6.43984Z"
                fill="#FE9A00"
              />
            </svg>
          </span>
        );
      case "check-green":
        return (
          <span className="check-green-icon notification-info-icon">
            <svg
              width="20"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.0499 8.7498C13.1604 8.62603 13.2172 8.46343 13.2078 8.29778C13.1984 8.13212 13.1236 7.97697 12.9999 7.86646C12.8761 7.75596 12.7135 7.69914 12.5478 7.70852C12.3822 7.7179 12.227 7.7927 12.1165 7.91646L9.60736 10.7281L8.88236 9.91646C8.77156 9.79403 8.6168 9.72041 8.4519 9.71171C8.287 9.703 8.12535 9.75991 8.00227 9.87C7.87919 9.98009 7.80468 10.1344 7.79502 10.2993C7.78535 10.4641 7.84132 10.6261 7.95069 10.7498L9.14069 12.0831C9.19932 12.1488 9.27116 12.2014 9.35151 12.2373C9.43186 12.2733 9.5189 12.2919 9.60694 12.2919C9.69498 12.2919 9.78202 12.2733 9.86237 12.2373C9.94272 12.2014 10.0146 12.1488 10.0732 12.0831L13.0499 8.7498Z"
                fill="#00BC7D"
              />
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M10.5 1.0415C9.71917 1.0415 8.9725 1.29734 7.76917 1.709L7.16333 1.91734C5.92917 2.339 4.97583 2.66567 4.29417 2.94234C3.95083 3.0815 3.655 3.21734 3.4175 3.35484C3.18833 3.48817 2.95917 3.654 2.80333 3.8765C2.64917 4.09567 2.56917 4.36567 2.51917 4.6265C2.46667 4.8965 2.43583 5.22067 2.415 5.59317C2.375 6.3315 2.375 7.34484 2.375 8.65817V9.99234C2.375 15.0765 6.215 17.514 8.49833 18.5123L8.52083 18.5215C8.80417 18.6457 9.07083 18.7615 9.37667 18.8398C9.69917 18.9232 10.0425 18.9582 10.5 18.9582C10.9583 18.9582 11.3 18.9232 11.6233 18.8398C11.9292 18.7615 12.1958 18.6457 12.4783 18.5215L12.5017 18.5123C14.785 17.514 18.625 15.0765 18.625 9.99234V8.65817C18.625 7.34484 18.625 6.3315 18.585 5.59234C18.5712 5.2682 18.5364 4.9453 18.4808 4.62567C18.4308 4.36567 18.3508 4.09567 18.1975 3.87567C18.0408 3.654 17.8117 3.48817 17.5825 3.35484C17.2995 3.19859 17.0066 3.0608 16.7058 2.94234C16.0242 2.66567 15.0708 2.339 13.8367 1.91734L13.2308 1.709C12.0275 1.29734 11.2808 1.0415 10.5 1.0415ZM8.06667 2.92817C9.4125 2.46817 9.95167 2.2915 10.5 2.2915C11.0483 2.2915 11.5875 2.46817 12.9333 2.92817L13.41 3.0915C14.6708 3.52317 15.59 3.83817 16.235 4.09984C16.5575 4.2315 16.7908 4.3415 16.955 4.4365C17.035 4.48317 17.0928 4.52178 17.1283 4.55234C17.16 4.57734 17.1725 4.59234 17.1742 4.594C17.1851 4.61361 17.194 4.63427 17.2008 4.65567C17.2175 4.70123 17.235 4.77011 17.2533 4.86234C17.2994 5.12622 17.3273 5.39296 17.3367 5.66067C17.375 6.3615 17.375 7.33984 17.375 8.68067V9.99234C17.375 14.3057 14.15 16.4282 12.0008 17.3665C11.6917 17.5015 11.5125 17.5782 11.3133 17.629C11.1233 17.6782 10.8917 17.7082 10.5 17.7082C10.1083 17.7082 9.87667 17.6782 9.68667 17.629C9.48667 17.579 9.30833 17.5015 8.99917 17.3665C6.84917 16.4282 3.625 14.3048 3.625 9.99234V8.68067C3.625 7.33984 3.625 6.3615 3.66333 5.66067C3.67275 5.39296 3.70059 5.12622 3.74667 4.86234C3.76444 4.77011 3.78194 4.70123 3.79917 4.65567C3.81417 4.614 3.82417 4.59734 3.82583 4.594C3.83978 4.57872 3.85513 4.56477 3.87167 4.55234C3.90722 4.52178 3.965 4.48289 4.045 4.43567C4.20917 4.34067 4.4425 4.2315 4.765 4.10067C5.41 3.83817 6.32917 3.52317 7.59 3.09234L8.06667 2.92817Z"
                fill="#00BC7D"
              />
            </svg>
          </span>
        );
      default:
        return <Calendar size={20} className="dl-notification-icon-blue" />;
    }
  };

  const LayoutDashboard = () => (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.14258 4.5V9.5H11.3092V2H4.64258C3.26424 2 2.14258 3.12167 2.14258 4.5ZM9.64258 7.83333H3.80924V4.5C3.80924 4.04 4.18258 3.66667 4.64258 3.66667H9.64258V7.83333ZM12.9759 22H19.6426C21.0209 22 22.1426 20.8783 22.1426 19.5V14.5H12.9759V22ZM14.6426 16.1667H20.4759V19.5C20.4759 19.96 20.1026 20.3333 19.6426 20.3333H14.6426V16.1667ZM19.6426 2H12.9759V12.8333H22.1426V4.5C22.1426 3.12167 21.0209 2 19.6426 2ZM20.4759 11.1667H14.6426V3.66667H19.6426C20.1026 3.66667 20.4759 4.04 20.4759 4.5V11.1667ZM2.14258 19.5C2.14258 20.8783 3.26424 22 4.64258 22H11.3092V11.1667H2.14258V19.5ZM3.80924 12.8333H9.64258V20.3333H4.64258C4.18258 20.3333 3.80924 19.96 3.80924 19.5V12.8333Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const Calendar = () => (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.14258 1.75C7.34149 1.75 7.53226 1.82902 7.67291 1.96967C7.81356 2.11032 7.89258 2.30109 7.89258 2.5V3.263C8.55458 3.25 9.28358 3.25 10.0866 3.25H14.1986C15.0016 3.25 15.7306 3.25 16.3926 3.263V2.5C16.3926 2.30109 16.4716 2.11032 16.6122 1.96967C16.7529 1.82902 16.9437 1.75 17.1426 1.75C17.3415 1.75 17.5323 1.82902 17.6729 1.96967C17.8136 2.11032 17.8926 2.30109 17.8926 2.5V3.327C18.1526 3.347 18.3989 3.37233 18.6316 3.403C19.8036 3.561 20.7526 3.893 21.5016 4.641C22.2496 5.39 22.5816 6.339 22.7396 7.511C22.7889 7.88567 22.8246 8.29567 22.8466 8.741C22.901 8.88855 22.9076 9.04947 22.8656 9.201C22.8926 10.002 22.8926 10.913 22.8926 11.944V14.056C22.8926 15.894 22.8926 17.35 22.7396 18.489C22.5816 19.661 22.2496 20.61 21.5016 21.359C20.7526 22.107 19.8036 22.439 18.6316 22.597C17.4916 22.75 16.0366 22.75 14.1986 22.75H10.0866C8.24858 22.75 6.79258 22.75 5.65358 22.597C4.48158 22.439 3.53258 22.107 2.78358 21.359C2.03558 20.61 1.70358 19.661 1.54558 18.489C1.39258 17.349 1.39258 15.894 1.39258 14.056V11.944C1.39258 10.913 1.39258 10.002 1.41958 9.2C1.37785 9.04836 1.38485 8.88744 1.43958 8.74C1.46091 8.29533 1.49624 7.88567 1.54558 7.511C1.70358 6.339 2.03558 5.39 2.78358 4.641C3.53258 3.893 4.48158 3.561 5.65358 3.403C5.88691 3.37233 6.13324 3.347 6.39258 3.327V2.5C6.39258 2.30109 6.4716 2.11032 6.61225 1.96967C6.7529 1.82902 6.94367 1.75 7.14258 1.75ZM2.90558 9.75C2.89258 10.403 2.89258 11.146 2.89258 12V14C2.89258 15.907 2.89458 17.262 3.03258 18.29C3.16758 19.295 3.42158 19.875 3.84458 20.298C4.26758 20.721 4.84758 20.975 5.85358 21.11C6.88158 21.248 8.23558 21.25 10.1426 21.25H14.1426C16.0496 21.25 17.4046 21.248 18.4326 21.11C19.4376 20.975 20.0176 20.721 20.4406 20.298C20.8636 19.875 21.1176 19.295 21.2526 18.289C21.3906 17.262 21.3926 15.907 21.3926 14V12C21.3926 11.146 21.3926 10.403 21.3796 9.75H2.90558ZM21.3106 8.25H2.97458C2.99058 8.06067 3.00991 7.88067 3.03258 7.71C3.16758 6.705 3.42158 6.125 3.84458 5.702C4.26758 5.279 4.84758 5.025 5.85358 4.89C6.88158 4.752 8.23558 4.75 10.1426 4.75H14.1426C16.0496 4.75 17.4046 4.752 18.4326 4.89C19.4376 5.025 20.0176 5.279 20.4406 5.702C20.8636 6.125 21.1176 6.705 21.2526 7.711C21.2752 7.881 21.2946 8.06067 21.3106 8.25ZM16.6426 15.75C16.4437 15.75 16.2529 15.829 16.1122 15.9697C15.9716 16.1103 15.8926 16.3011 15.8926 16.5C15.8926 16.6989 15.9716 16.8897 16.1122 17.0303C16.2529 17.171 16.4437 17.25 16.6426 17.25C16.8415 17.25 17.0323 17.171 17.1729 17.0303C17.3136 16.8897 17.3926 16.6989 17.3926 16.5C17.3926 16.3011 17.3136 16.1103 17.1729 15.9697C17.0323 15.829 16.8415 15.75 16.6426 15.75ZM14.3926 16.5C14.3926 16.2045 14.4508 15.9119 14.5638 15.639C14.6769 15.366 14.8427 15.1179 15.0516 14.909C15.2605 14.7001 15.5086 14.5343 15.7815 14.4213C16.0545 14.3082 16.3471 14.25 16.6426 14.25C16.9381 14.25 17.2306 14.3082 17.5036 14.4213C17.7766 14.5343 18.0246 14.7001 18.2336 14.909C18.4425 15.1179 18.6082 15.366 18.7213 15.639C18.8344 15.9119 18.8926 16.2045 18.8926 16.5C18.8926 17.0967 18.6555 17.669 18.2336 18.091C17.8116 18.5129 17.2393 18.75 16.6426 18.75C16.0458 18.75 15.4735 18.5129 15.0516 18.091C14.6296 17.669 14.3926 17.0967 14.3926 16.5Z"
        fill="#2D2D37"
      />
    </svg>
  );
  // eslint-disable-next-line
  const Users = () => (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_3127_782)">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.1426 1.25C10.8828 1.25 9.67462 1.75044 8.78382 2.64124C7.89302 3.53204 7.39258 4.74022 7.39258 6C7.39258 7.25978 7.89302 8.46796 8.78382 9.35876C9.67462 10.2496 10.8828 10.75 12.1426 10.75C13.4024 10.75 14.6105 10.2496 15.5013 9.35876C16.3921 8.46796 16.8926 7.25978 16.8926 6C16.8926 4.74022 16.3921 3.53204 15.5013 2.64124C14.6105 1.75044 13.4024 1.25 12.1426 1.25ZM8.89258 6C8.89258 5.13805 9.23499 4.3114 9.84448 3.7019C10.454 3.09241 11.2806 2.75 12.1426 2.75C13.0045 2.75 13.8312 3.09241 14.4407 3.7019C15.0502 4.3114 15.3926 5.13805 15.3926 6C15.3926 6.86195 15.0502 7.6886 14.4407 8.2981C13.8312 8.90759 13.0045 9.25 12.1426 9.25C11.2806 9.25 10.454 8.90759 9.84448 8.2981C9.23499 7.6886 8.89258 6.86195 8.89258 6Z"
          fill="#2D2D37"
        />
        <path
          d="M18.1426 3.25C17.9437 3.25 17.7529 3.32902 17.6122 3.46967C17.4716 3.61032 17.3926 3.80109 17.3926 4C17.3926 4.19891 17.4716 4.38968 17.6122 4.53033C17.7529 4.67098 17.9437 4.75 18.1426 4.75C19.5196 4.75 20.3926 5.656 20.3926 6.5C20.3926 7.344 19.5196 8.25 18.1426 8.25C17.9437 8.25 17.7529 8.32902 17.6122 8.46967C17.4716 8.61032 17.3926 8.80109 17.3926 9C17.3926 9.19891 17.4716 9.38968 17.6122 9.53033C17.7529 9.67098 17.9437 9.75 18.1426 9.75C20.0796 9.75 21.8926 8.417 21.8926 6.5C21.8926 4.583 20.0796 3.25 18.1426 3.25ZM6.89258 4C6.89258 3.80109 6.81356 3.61032 6.67291 3.46967C6.53226 3.32902 6.34149 3.25 6.14258 3.25C4.20558 3.25 2.39258 4.583 2.39258 6.5C2.39258 8.417 4.20558 9.75 6.14258 9.75C6.34149 9.75 6.53226 9.67098 6.67291 9.53033C6.81356 9.38968 6.89258 9.19891 6.89258 9C6.89258 8.80109 6.81356 8.61032 6.67291 8.46967C6.53226 8.32902 6.34149 8.25 6.14258 8.25C4.76658 8.25 3.89258 7.344 3.89258 6.5C3.89258 5.656 4.76658 4.75 6.14258 4.75C6.34149 4.75 6.53226 4.67098 6.67291 4.53033C6.81356 4.38968 6.89258 4.19891 6.89258 4Z"
          fill="#2D2D37"
        />
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M12.1426 12.25C10.3586 12.25 8.70858 12.73 7.48358 13.547C6.26358 14.361 5.39258 15.567 5.39258 17C5.39258 18.433 6.26358 19.64 7.48358 20.453C8.70858 21.269 10.3586 21.75 12.1426 21.75C13.9266 21.75 15.5766 21.27 16.8016 20.453C18.0216 19.639 18.8926 18.433 18.8926 17C18.8926 15.567 18.0206 14.36 16.8016 13.547C15.5766 12.731 13.9266 12.25 12.1426 12.25ZM6.89258 17C6.89258 16.224 7.36458 15.43 8.31558 14.796C9.26258 14.165 10.6136 13.75 12.1426 13.75C13.6726 13.75 15.0226 14.165 15.9696 14.796C16.9206 15.43 17.3926 16.224 17.3926 17C17.3926 17.776 16.9206 18.57 15.9696 19.204C15.0226 19.835 13.6716 20.25 12.1426 20.25C10.6126 20.25 9.26258 19.835 8.31558 19.204C7.36458 18.57 6.89258 17.776 6.89258 17Z"
          fill="#2D2D37"
        />
        <path
          d="M19.4096 13.84C19.4306 13.7437 19.4703 13.6524 19.5266 13.5714C19.5829 13.4904 19.6547 13.4213 19.7377 13.3681C19.8208 13.3149 19.9135 13.2785 20.0106 13.2612C20.1077 13.2439 20.2072 13.2458 20.3036 13.267C21.2646 13.478 22.1316 13.859 22.7756 14.386C23.4186 14.912 23.8926 15.636 23.8926 16.5C23.8926 17.365 23.4186 18.088 22.7756 18.614C22.1316 19.141 21.2656 19.522 20.3036 19.733C20.2073 19.7542 20.1078 19.7561 20.0108 19.7388C19.9138 19.7215 19.8211 19.6853 19.7381 19.6322C19.6551 19.5791 19.5834 19.5101 19.527 19.4293C19.4706 19.3484 19.4307 19.2573 19.4096 19.161C19.3884 19.0648 19.3865 18.9653 19.4038 18.8683C19.4211 18.7712 19.4573 18.6786 19.5104 18.5956C19.5635 18.5125 19.6325 18.4408 19.7133 18.3844C19.7942 18.3281 19.8853 18.2882 19.9816 18.267C20.7746 18.094 21.4076 17.795 21.8256 17.453C22.2436 17.111 22.3926 16.776 22.3926 16.5C22.3926 16.224 22.2436 15.89 21.8256 15.547C21.4076 15.204 20.7746 14.907 19.9816 14.733C19.8853 14.7119 19.7941 14.6721 19.7132 14.6157C19.6324 14.5594 19.5634 14.4876 19.5103 14.4046C19.4571 14.3216 19.4209 14.2289 19.4036 14.1318C19.3863 14.0348 19.3884 13.9353 19.4096 13.839M3.98258 13.267C4.17698 13.2246 4.38028 13.2611 4.54775 13.3686C4.71522 13.476 4.83314 13.6456 4.87558 13.84C4.91801 14.0344 4.88148 14.2377 4.77402 14.4052C4.66657 14.5727 4.49698 14.6906 4.30258 14.733C3.51058 14.906 2.87758 15.205 2.45958 15.547C2.04158 15.889 1.89258 16.224 1.89258 16.5C1.89258 16.776 2.04158 17.11 2.45958 17.453C2.87758 17.796 3.51058 18.093 4.30358 18.267C4.49798 18.3097 4.66746 18.4279 4.77473 18.5956C4.882 18.7632 4.91828 18.9666 4.87558 19.161C4.83288 19.3554 4.7147 19.5249 4.54704 19.6322C4.37939 19.7395 4.17598 19.7757 3.98158 19.733C3.01958 19.522 2.15358 19.141 1.50958 18.614C0.866578 18.088 0.392578 17.364 0.392578 16.5C0.392578 15.635 0.866578 14.912 1.50958 14.386C2.15358 13.859 3.02058 13.478 3.98258 13.267Z"
          fill="#2D2D37"
        />
      </g>
      <defs>
        <clipPath id="clip0_3127_782">
          <rect
            width="24"
            height="24"
            fill="white"
            transform="translate(0.142578)"
          />
        </clipPath>
      </defs>
    </svg>
  );

  const CreditCard = () => (
    <svg
      width="25"
      height="24"
      viewBox="0 0 25 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M21.0666 11.7502H18.3726C16.9486 11.7502 15.8916 12.8092 15.8916 14.0002C15.8916 15.1912 16.9486 16.2502 18.3716 16.2502H21.0896C21.2956 16.2372 21.3846 16.0982 21.3916 16.0142V11.9862C21.3846 11.9022 21.2956 11.7632 21.0896 11.7512L21.0666 11.7502ZM20.9926 10.2502C21.0592 10.2495 21.1226 10.2508 21.1826 10.2542C22.0526 10.3072 22.8236 10.9642 22.8886 11.8822C22.8926 11.9422 22.8926 12.0072 22.8926 12.0672V15.9332C22.8926 15.9932 22.8926 16.0582 22.8886 16.1182C22.8236 17.0362 22.0526 17.6932 21.1816 17.7472C21.1222 17.7498 21.0589 17.7508 20.9916 17.7502H18.3736C16.2286 17.7502 14.3926 16.1222 14.3926 14.0002C14.3926 11.8782 16.2286 10.2502 18.3726 10.2502H20.9926Z"
        fill="#2D2D37"
      />
      <path
        d="M19.1426 14C19.1426 14.2652 19.0372 14.5196 18.8497 14.7071C18.6621 14.8946 18.4078 15 18.1426 15C17.8774 15 17.623 14.8946 17.4355 14.7071C17.2479 14.5196 17.1426 14.2652 17.1426 14C17.1426 13.7348 17.2479 13.4804 17.4355 13.2929C17.623 13.1054 17.8774 13 18.1426 13C18.4078 13 18.6621 13.1054 18.8497 13.2929C19.0372 13.4804 19.1426 13.7348 19.1426 14Z"
        fill="#2D2D37"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.9926 10.25C21.0592 10.2494 21.1226 10.2507 21.1826 10.254C21.4076 10.267 21.6256 10.321 21.8276 10.41C21.7206 8.80402 21.4256 7.56603 20.5016 6.64103C19.7526 5.89302 18.8036 5.56103 17.6316 5.40303L17.5896 5.39803L17.5576 5.37503L13.8216 2.89803C13.1734 2.47547 12.4163 2.25049 11.6426 2.25049C10.8688 2.25049 10.1118 2.47547 9.46358 2.89803L5.72858 5.37503L5.69558 5.39803L5.65358 5.40303C4.48158 5.56103 3.53258 5.89302 2.78358 6.64103C2.03558 7.39003 1.70358 8.33903 1.54558 9.51102C1.39258 10.651 1.39258 12.106 1.39258 13.944V14.056C1.39258 15.894 1.39258 17.35 1.54558 18.489C1.70358 19.661 2.03558 20.61 2.78358 21.359C3.53258 22.107 4.48158 22.439 5.65358 22.597C6.79358 22.75 8.24858 22.75 10.0866 22.75H13.1986C15.0366 22.75 16.4926 22.75 17.6316 22.597C18.8036 22.439 19.7526 22.107 20.5016 21.359C21.4256 20.434 21.7206 19.196 21.8276 17.589C21.6242 17.6797 21.4089 17.7324 21.1816 17.747C21.1222 17.7497 21.0589 17.7507 20.9916 17.75H20.3106C20.1966 19.092 19.9396 19.8 19.4406 20.298C19.0176 20.721 18.4376 20.975 17.4316 21.11C16.4046 21.248 15.0496 21.25 13.1426 21.25H10.1426C8.23558 21.25 6.88158 21.248 5.85258 21.11C4.84758 20.975 4.26758 20.721 3.84458 20.298C3.42158 19.875 3.16758 19.295 3.03258 18.289C2.89458 17.262 2.89258 15.907 2.89258 14C2.89258 12.093 2.89458 10.739 3.03258 9.71003C3.16758 8.70503 3.42158 8.12503 3.84458 7.70203C4.26758 7.27903 4.84758 7.02503 5.85358 6.89003C6.88158 6.75203 8.23558 6.75003 10.1426 6.75003H13.1426C15.0496 6.75003 16.4046 6.75203 17.4326 6.89003C18.4376 7.02503 19.0176 7.27903 19.4406 7.70203C19.9396 8.20003 20.1966 8.90903 20.3106 10.25H20.9926ZM10.0866 5.25003H13.1986C13.7152 5.25003 14.2019 5.25102 14.6586 5.25303L12.9926 4.14803C12.1926 3.61803 11.0926 3.61803 10.2926 4.14803L8.62558 5.25303C9.08291 5.25036 9.56958 5.24936 10.0856 5.25003"
        fill="#2D2D37"
      />
      <path
        d="M6.14258 9.25C5.94367 9.25 5.7529 9.32902 5.61225 9.46967C5.4716 9.61032 5.39258 9.80109 5.39258 10C5.39258 10.1989 5.4716 10.3897 5.61225 10.5303C5.7529 10.671 5.94367 10.75 6.14258 10.75H10.1426C10.3415 10.75 10.5323 10.671 10.6729 10.5303C10.8136 10.3897 10.8926 10.1989 10.8926 10C10.8926 9.80109 10.8136 9.61032 10.6729 9.46967C10.5323 9.32902 10.3415 9.25 10.1426 9.25H6.14258Z"
        fill="#2D2D37"
      />
      <path
        d="M19.1426 14C19.1426 14.2652 19.0372 14.5196 18.8497 14.7071C18.6621 14.8946 18.4078 15 18.1426 15C17.8774 15 17.623 14.8946 17.4355 14.7071C17.2479 14.5196 17.1426 14.2652 17.1426 14C17.1426 13.7348 17.2479 13.4804 17.4355 13.2929C17.623 13.1054 17.8774 13 18.1426 13C18.4078 13 18.6621 13.1054 18.8497 13.2929C19.0372 13.4804 19.1426 13.7348 19.1426 14Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const productCatalogueIcon = () => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.945 1.25H15.055C16.422 1.25 17.525 1.25 18.392 1.367C19.292 1.487 20.05 1.747 20.652 2.348C21.254 2.95 21.512 3.708 21.634 4.608C21.75 5.475 21.75 6.578 21.75 7.945V16.055C21.75 17.422 21.75 18.525 21.634 19.392C21.512 20.292 21.254 21.05 20.652 21.652C20.05 22.254 19.292 22.512 18.392 22.634C17.525 22.75 16.422 22.75 15.055 22.75H8.945L8.046 22.749C8.01269 22.7507 7.97931 22.7507 7.946 22.749C7.028 22.742 6.253 22.72 5.608 22.634C4.708 22.512 3.95 22.254 3.348 21.652C2.746 21.05 2.488 20.292 2.367 19.392C2.27 18.677 2.254 17.802 2.251 16.75H2C1.80109 16.75 1.61032 16.671 1.46967 16.5303C1.32902 16.3897 1.25 16.1989 1.25 16C1.25 15.8011 1.32902 15.6103 1.46967 15.4697C1.61032 15.329 1.80109 15.25 2 15.25H2.25V12.75H2C1.80109 12.75 1.61032 12.671 1.46967 12.5303C1.32902 12.3897 1.25 12.1989 1.25 12C1.25 11.8011 1.32902 11.6103 1.46967 11.4697C1.61032 11.329 1.80109 11.25 2 11.25H2.25V8.75H2C1.80109 8.75 1.61032 8.67098 1.46967 8.53033C1.32902 8.38968 1.25 8.19891 1.25 8C1.25 7.80109 1.32902 7.61032 1.46967 7.46967C1.61032 7.32902 1.80109 7.25 2 7.25H2.25C2.254 6.198 2.27 5.323 2.367 4.608C2.487 3.708 2.747 2.95 3.348 2.348C3.95 1.746 4.708 1.488 5.608 1.367C6.475 1.25 7.578 1.25 8.945 1.25ZM3.75 8.75H4C4.19891 8.75 4.38968 8.67098 4.53033 8.53033C4.67098 8.38968 4.75 8.19891 4.75 8C4.75 7.80109 4.67098 7.61032 4.53033 7.46967C4.38968 7.32902 4.19891 7.25 4 7.25H3.75C3.754 6.204 3.77 5.424 3.853 4.808C3.952 4.074 4.133 3.686 4.409 3.409C4.686 3.132 5.074 2.952 5.809 2.853C6.209 2.799 6.681 2.773 7.25 2.761V21.24C6.768 21.2361 6.28659 21.2054 5.808 21.148C5.074 21.049 4.686 20.868 4.409 20.592C4.132 20.315 3.952 19.927 3.853 19.192C3.77 18.577 3.754 17.797 3.751 16.751H4C4.19891 16.751 4.38968 16.672 4.53033 16.5313C4.67098 16.3907 4.75 16.1999 4.75 16.001C4.75 15.8021 4.67098 15.6113 4.53033 15.4707C4.38968 15.33 4.19891 15.251 4 15.251H3.75V12.751H4C4.19891 12.751 4.38968 12.672 4.53033 12.5313C4.67098 12.3907 4.75 12.1999 4.75 12.001C4.75 11.8021 4.67098 11.6113 4.53033 11.4707C4.38968 11.33 4.19891 11.251 4 11.251H3.75V8.75ZM8.75 21.25H15C16.435 21.25 17.436 21.248 18.192 21.147C18.926 21.048 19.314 20.867 19.591 20.591C19.868 20.314 20.048 19.926 20.147 19.191C20.248 18.436 20.25 17.435 20.25 16V8C20.25 6.565 20.248 5.563 20.147 4.808C20.048 4.074 19.867 3.686 19.591 3.409C19.314 3.132 18.926 2.952 18.191 2.853C17.436 2.752 16.435 2.75 15 2.75H8.75V21.25ZM10.75 6.5C10.75 6.30109 10.829 6.11032 10.9697 5.96967C11.1103 5.82902 11.3011 5.75 11.5 5.75H16.5C16.6989 5.75 16.8897 5.82902 17.0303 5.96967C17.171 6.11032 17.25 6.30109 17.25 6.5C17.25 6.69891 17.171 6.88968 17.0303 7.03033C16.8897 7.17098 16.6989 7.25 16.5 7.25H11.5C11.3011 7.25 11.1103 7.17098 10.9697 7.03033C10.829 6.88968 10.75 6.69891 10.75 6.5ZM10.75 10C10.75 9.80109 10.829 9.61032 10.9697 9.46967C11.1103 9.32902 11.3011 9.25 11.5 9.25H16.5C16.6989 9.25 16.8897 9.32902 17.0303 9.46967C17.171 9.61032 17.25 9.80109 17.25 10C17.25 10.1989 17.171 10.3897 17.0303 10.5303C16.8897 10.671 16.6989 10.75 16.5 10.75H11.5C11.3011 10.75 11.1103 10.671 10.9697 10.5303C10.829 10.3897 10.75 10.1989 10.75 10Z"
        fill="#2D2D37"
      />
    </svg>
  );

  const navItems = [
    {
      id: "dashboard",
      label: t("pharmacy.sidebar.dashboard"),
      icon: LayoutDashboard,
      path: "/pharmacy-dashboard",
    },
    {
      id: "orders",
      label: t("pharmacy.sidebar.orders"),
      icon: Calendar,
      children: [
        {
          id: "all-order",
          label: t("pharmacy.sidebar.allOrders"),
          path: "/pharmacy-all-order",
        },
        {
          id: "pharmacy-orders-request",
          label: t("pharmacy.sidebar.orderRequest"),
          path: "/pharmacy-orders-request",
        },
        {
          id: "pharmacy-orders-history",
          label: t("pharmacy.sidebar.orderHistory"),
          path: "/pharmacy-orders-history",
        },
      ],
    },
    // {
    //   id: "inventory",
    //   label: "Inventory",
    //   icon: Users,
    //   children: [
    //     {
    //       id: "products",
    //       label: "Products List",
    //       path: "/pharmacy-inventory-products",
    //     },
    //     { id: "stock", label: "Invoice", path: "/pharmacy-inventory-stock" },
    //   ],
    // },
    {
      id: "productCatalogue",
      label: t("pharmacy.sidebar.productCatalogue"),
      icon: productCatalogueIcon,
      path: "/pharmacy-add-medicine-to-inventory",
    },
    {
      id: "account",
      label: t("pharmacy.sidebar.accountPayments"),
      icon: CreditCard,
      path: "/pharmacy-account-payments",
    },
  ];

  // Helper function to check if a path is active
  const isPathActive = (path) => {
    return location.pathname === path;
  };

  // Helper function to check if any child path is active
  const isAnyChildActive = (children) => {
    return children?.some((child) => location.pathname === child.path);
  };

  return (
    <div className={styles["dl-dashboard"]}>
      {/* Header */}

      <header className={styles["dl-header"]}>
        <div className={styles["dl-header-left"]}>
          <button
            className={styles["dl-mobile-menu-btn"]}
            onClick={toggleSidebar}
          >
            <Menu size={24} />
          </button>
          <div className={styles["dl-logo"]}>
            <img src={Logo} alt="TIRYAQ" />
          </div>
        </div>

        <div className={styles["dl-header-center"]}>
          {sessionStorage.getItem("usr_role_id") !== "5" && (
            <div className={styles["dl-clinic-selector"]}>
              <span>{t("currentClinic")}</span>
              <select>
                <option>Baghdad Children's Hospital</option>
                <option>Al-Rashid Medical Center</option>
                <option>Ibn Sina Hospital</option>
              </select>
              <ChevronDown className={styles["dl-dropdown-icon"]} />
            </div>
          )}
        </div>

        <div className={styles["dl-header-right"]}>
          {/* <button className={styles["dl-settings-btn"]}>
                    <Settings size={20} />
                </button> */}
          {/* <button className={styles["dl-notification-btn"]}>
                </button> */}
          <div className={styles["dl-notification-container"]}>
            <button
              className={styles["dl-notification-btn"]}
              onClick={togglePharmacyNotifications}
            >
              <IoNotifications size={20} />
              {pharmacyUnreadCount > 0 && (
                <span className={styles["dl-notification-badge"]}>
                  {pharmacyUnreadCount > 99 ? "99+" : pharmacyUnreadCount}
                </span>
              )}
            </button>
            <PharmacyNotificationsPanel
              isOpen={isPharmacyNotificationsOpen}
              onClose={() => setIsPharmacyNotificationsOpen(false)}
            />
          </div>

          {/* Language dropdown hidden in the pharmacy header (per request) */}
          {false && (
            <div className={styles["dl-language-selector"]}>
              <button
                className={styles["dl-language-btn"]}
                onClick={() =>
                  setIsLanguageDropdownOpen(!isLanguageDropdownOpen)
                }
              >
                <span className={styles["dl-language-icon"]}>
                  <HiMiniLanguage />{" "}
                </span>
                <span className={styles["dl-language-text"]}>
                  {selectedLanguage}
                </span>
                <ChevronDown size={16} className={`${styles["down-icon"]}`} />
              </button>
              {isLanguageDropdownOpen && (
                <div className={styles["dl-language-dropdown"]}>
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      className={`${styles["dl-language-option"]} ${
                        selectedLanguage === lang.name ? styles["active"] : ""
                      }`}
                      onClick={() => handleLanguageSelect(lang)}
                    >
                      <span className={styles["dl-flag"]}>
                        <img src={`/flags/${lang.flag}`} alt="" width={20} />
                      </span>
                      <span>{lang.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <div className={styles["dl-user-profile"]}>
            <div className={`dropdown ${styles["my-profile-dropdown"]}`}>
              <span
                className={`dropdown-toggle ${styles["dropdown-toggle"]}`}
                type="button"
                id="dropdownMenuButton"
                data-bs-toggle="dropdown"
                aria-expanded="false"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                <img
                  src={headerAvatar}
                  alt={t("pharmacy.sidebar.avatarAlt")}
                  className={styles["dl-avatar"]}
                  onError={(e) => {
                    if (e.target.src !== defaultAvatar) {
                      e.target.src = defaultAvatar;
                      setHeaderAvatar(defaultAvatar);
                      sessionStorage.removeItem("pharmacy_avatar_url");
                      fetchPharmacyAvatar();
                    }
                  }}
                />
                {headerName && (
                  <span
                    title={headerName}
                    style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#1f1f1f",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "160px",
                      display: "inline-block",
                      verticalAlign: "middle",
                      marginInlineStart: "8px",
                      userSelect: "none",
                    }}
                  >
                    {headerName}
                  </span>
                )}
                <ChevronDown
                  size={18}
                  style={{
                    marginInlineStart: "4px",
                    color: "#1f1f1f",
                    flexShrink: 0,
                  }}
                />
              </span>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="dropdownMenuButton"
                style={{
                  minWidth: "230px",
                  padding: "6px 0",
                  borderRadius: "12px",
                  border: "1px solid #eef0f2",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                  marginTop: "8px",
                }}
              >
                <li style={{ padding: "10px 16px 12px" }}>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: "#1f1f1f",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                    }}
                  >
                    {headerName ||
                      t("pharmacy.sidebar.pharmacyNameFallback", "Pharmacy")}
                  </div>
                  {headerPhone && (
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#8a8a8a",
                        marginTop: "2px",
                      }}
                    >
                      {headerPhone}
                    </div>
                  )}
                </li>
                <li>
                  <hr
                    className="dropdown-divider"
                    style={{ margin: "2px 0" }}
                  />
                </li>
                <li>
                  <Link
                    className={`dropdown-item ${styles["dropdown-item"]}`}
                    to="/pharmacy-profile"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      fontSize: "14px",
                    }}
                  >
                    <User size={16} />{" "}
                    {t("pharmacy.sidebar.profile", "Profile")}
                  </Link>
                </li>
                <li>
                  <button
                    type="button"
                    className={`dropdown-item ${styles["dropdown-item"]}`}
                    onClick={onLogout || logoutHandler}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      padding: "10px 16px",
                      fontSize: "14px",
                      width: "100%",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#dc2626",
                    }}
                  >
                    <LogOut size={16} />{" "}
                    {t("pharmacy.sidebar.logout", "Logout")}
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      {/* Sidebar */}
      <aside
        className={`${styles["dl-sidebar"]} ${
          isSidebarOpen ? styles["open"] : ""
        }`}
      >
        <nav className={styles["dl-nav"]}>
          {navItems.map((item) => {
            const IconComponent = item.icon;
            // If menu has children
            if (item.children) {
              const hasActiveChild = isAnyChildActive(item.children);
              return (
                <div key={item.id} className={styles["dl-nav-group"]}>
                  <button
                    className={`${styles["dl-nav-item"]} ${
                      styles["dl-submenu-list"]
                    } ${openMenu === item.id || hasActiveChild ? styles["open"] : ""} ${
                      hasActiveChild ? styles["active"] : ""
                    }`}
                    onClick={() =>
                      setOpenMenu(openMenu === item.id ? null : item.id)
                    }
                  >
                    <span className={`${styles["dl-submenu-list-title"]}`}>
                      <IconComponent size={20} />
                      <span>{item.label}</span>
                    </span>

                    <ChevronDown
                      size={16}
                      className={`${
                        openMenu === item.id || hasActiveChild
                          ? styles["rotate"]
                          : ""
                      }`}
                    />
                  </button>

                  {(openMenu === item.id || hasActiveChild) && (
                    <div className={styles["dl-submenu"]}>
                      {item.children.map((child) => (
                        <button
                          key={child.id}
                          className={`${styles["dl-sub-item"]} ${
                            isPathActive(child.path) ? styles["active"] : ""
                          }`}
                          onClick={() => {
                            navigate(child.path);
                            setIsSidebarOpen(false);
                          }}
                        >
                          <span>{child.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            // If menu has no children
            return (
              <button
                key={item.id}
                className={`${styles["dl-nav-item"]} ${
                  isPathActive(item.path) ? styles["active"] : ""
                }`}
                onClick={() => {
                  navigate(item.path);
                  setIsSidebarOpen(false);
                }}
              >
                <IconComponent size={20} />
                <span>{item.label}</span>
              </button>
            );
          })}
        </nav>

        <div className={`${styles["dl-promo-banner"]} d-none`}>
          <div className={styles["dl-promo-header"]}>
            <div className={styles["dl-promo-icon"]}>
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M13.3333 8H18.6667C22.4373 8 24.324 8 25.4947 9.172C26.6667 10.3427 26.6667 12.2293 26.6667 16V17.408C26.6667 20.6147 26.6667 22.2187 25.8707 23.508C25.0733 24.796 23.6387 25.5133 20.7707 26.948C18.4293 28.1187 17.2587 28.704 16 28.704C14.7413 28.704 13.5707 28.1187 11.2293 26.948C8.36134 25.5133 6.92667 24.796 6.12934 23.508C5.33334 22.2187 5.33334 20.6133 5.33334 17.408V16C5.33334 12.2293 5.33334 10.3427 6.50534 9.172C7.676 8 9.56267 8 13.3333 8ZM16 13.3333C15.6213 13.3333 15.368 13.7867 14.8613 14.6973L14.7307 14.932C14.5867 15.1907 14.5147 15.3187 14.4027 15.404C14.2893 15.4893 14.1493 15.5213 13.8693 15.584L13.616 15.6427C12.632 15.8653 12.14 15.976 12.0227 16.352C11.9053 16.728 12.2413 17.1213 12.912 17.9053L13.0853 18.108C13.276 18.3307 13.372 18.4413 13.4147 18.58C13.4573 18.7187 13.4427 18.8667 13.4147 19.164L13.388 19.4347C13.2867 20.4813 13.236 21.0053 13.5413 21.2373C13.848 21.4693 14.3093 21.2573 15.2307 20.8333L15.468 20.724C15.7307 20.604 15.8613 20.544 16 20.544C16.1387 20.544 16.2693 20.604 16.532 20.724L16.7693 20.8333C17.6907 21.2587 18.152 21.4693 18.4587 21.2373C18.7653 21.0053 18.7133 20.4813 18.612 19.4347L18.5853 19.164C18.5573 18.8667 18.5427 18.7187 18.5853 18.58C18.628 18.4413 18.724 18.3307 18.9147 18.108L19.088 17.9053C19.7587 17.1213 20.0947 16.7293 19.9773 16.352C19.86 15.976 19.368 15.8653 18.384 15.6427L18.1307 15.584C17.8507 15.5213 17.7107 15.4907 17.5973 15.404C17.4853 15.3187 17.4133 15.1907 17.2693 14.932L17.1387 14.6973C16.632 13.7867 16.3787 13.3333 16 13.3333Z"
                  fill="#F54900"
                />
                <path
                  d="M14.6667 2.66663H17.3333C19.848 2.66663 21.104 2.66663 21.8853 3.44796C22.4373 3.99996 22.5987 4.78929 22.6467 6.09196C21.5533 5.99863 20.2587 5.99863 18.7987 5.99996H13.2C11.74 5.99996 10.4467 5.99996 9.352 6.09196C9.39866 4.78929 9.56133 3.99863 10.112 3.44796C10.896 2.66663 12.1547 2.66663 14.6667 2.66663Z"
                  fill="#F54900"
                />
              </svg>
            </div>
            <h3>{t("pharmacy.sidebar.promo.title")}</h3>
          </div>
          <div className={styles["dl-promo-content"]}>
            <p>{t("pharmacy.sidebar.promo.description")}</p>
            <ul>
              <li>
                <span className={styles["dl-promo-content-icon"]}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.6667 7.99992C14.6667 11.6818 11.6819 14.6666 8 14.6666C4.3181 14.6666 1.33333 11.6818 1.33333 7.99992C1.33333 4.31802 4.3181 1.33325 8 1.33325C11.6819 1.33325 14.6667 4.31802 14.6667 7.99992ZM10.6869 5.9797C10.8821 6.17496 10.8821 6.49154 10.6869 6.6868L7.35355 10.0201C7.15829 10.2154 6.84171 10.2154 6.64644 10.0201L5.31311 8.68681C5.11785 8.49154 5.11785 8.17496 5.31311 7.9797C5.50837 7.78444 5.82496 7.78444 6.02022 7.9797L7 8.95948L8.48989 7.46959L9.97978 5.9797C10.175 5.78444 10.4916 5.78444 10.6869 5.9797Z"
                      fill="#F54900"
                    />
                  </svg>
                </span>
                {t("pharmacy.sidebar.promo.higherRankings")}
              </li>
              <li>
                <span className={styles["dl-promo-content-icon"]}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M14.6667 7.99992C14.6667 11.6818 11.6819 14.6666 8 14.6666C4.3181 14.6666 1.33333 11.6818 1.33333 7.99992C1.33333 4.31802 4.3181 1.33325 8 1.33325C11.6819 1.33325 14.6667 4.31802 14.6667 7.99992ZM10.6869 5.9797C10.8821 6.17496 10.8821 6.49154 10.6869 6.6868L7.35355 10.0201C7.15829 10.2154 6.84171 10.2154 6.64644 10.0201L5.31311 8.68681C5.11785 8.49154 5.11785 8.17496 5.31311 7.9797C5.50837 7.78444 5.82496 7.78444 6.02022 7.9797L7 8.95948L8.48989 7.46959L9.97978 5.9797C10.175 5.78444 10.4916 5.78444 10.6869 5.9797Z"
                      fill="#F54900"
                    />
                  </svg>
                </span>
                {t("pharmacy.sidebar.promo.featuredSections")}
              </li>
            </ul>
            <button className={styles["dl-promo-btn"]}>
              {t("pharmacy.sidebar.promo.learnMore")} <Info />
            </button>
          </div>
        </div>

        <div className={styles["dl-sidebar-bottom"]}>
          <Link to={"/pharmacy-chat"}>
            <button
              className={`${styles["dl-nav-item"]} ${
                isPathActive("/pharmacy-chat") ? styles["active"] : ""
              }`}
            >
              <BsChatText size={20} />
              <span>{t("pharmacy.sidebar.chat")}</span>
              {chatUnreadCount > 0 && (
                <span
                  style={{
                    marginLeft: "auto",
                    background: "#ef4444",
                    color: "#fff",
                    fontSize: "10px",
                    fontWeight: "bold",
                    borderRadius: "50%",
                    minWidth: "18px",
                    height: "18px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "0 4px",
                  }}
                >
                  {chatUnreadCount > 99 ? "99+" : chatUnreadCount}
                </span>
              )}
            </button>
          </Link>
          {/* <Link to={"/help-and-support"}>
                        <button className={`${styles["dl-nav-item"]} ${currentPage === "help-and-support" ? styles["active"] : ""}`}>
                            <HelpCircle size={20} />
                            <span>Help and Support</span>
                        </button>
                    </Link> */}
          <Link to={"/pharmacy-setting"}>
            <button
              className={`${styles["dl-nav-item"]} ${
                isPathActive("/pharmacy-setting") ? styles["active"] : ""
              }`}
            >
              <Settings size={20} /> {t("pharmacy.sidebar.settings")}
            </button>
          </Link>
          {/* <button className={styles["dl-nav-item"]}>
                        <Settings size={20} />
                    </button> */}
          <Link to={"/"}>
            <button
              className={`${styles["dl-nav-item"]} ${
                isPathActive("/") ? styles["active"] : ""
              }`}
              onClick={onLogout || logoutHandler}
            >
              <LogOut size={20} />
              <span>{t("pharmacy.sidebar.logout")}</span>
            </button>
          </Link>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className={styles["dl-sidebar-overlay"]}
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}

      {/* Main Content */}
      <main className={styles["dl-main-content"]}>
        {children}
      </main>

      {/* Minimized order-chat windows (bottom-right) */}
      <PharmacyChatDock />
    </div>
  );
};

export default PharmacyDashboardLayout;
